/**
 * TNE 2026, © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/components/layout/BandeauPartenaires.tsx
 *
 * Rôle :
 *   Bandeau de logos partenaires permanent (§13.5.4) affiché dans le footer
 *   de toutes les pages.
 *
 *   FIX 2026-05-07 : Remplacement du rendu 6 logos individuels (qui
 *   échouait à charger faute des PNG) par l'image composite officielle
 *   Stitch (`partners-ribbon.jpg`), pattern fidèle au design Stitch
 *   `app/Stitch Google/mon_espace_visiteur/code.html` ligne 204
 *   (« <img alt="Partenaires : Région Grand Est, Académie Nancy-Metz,
 *      Banque des Territoires, France 2030, Territoires Numériques
 *      Éducatifs" ... mix-blend-multiply opacity-90 hover:opacity-100 »).
 *   L'image composite contient les 5 logos officiels validés par la DRANE
 *   Nancy-Metz : Académie Nancy-Metz, La Vie en Vosges (CD88), Banque
 *   des Territoires, France 2030, TNE/École de la Confiance.
 *
 *   Si une autre liste est souhaitée plus tard (CDC §13.5 catégories
 *   institutionnelles + EdTech + financiers), passer la prop `partners`
 *   pour basculer sur le rendu legacy 6 logos individuels.
 *
 * Réutilisable :
 *   100 %. Pour une autre app DRANE, fournir une autre image composite
 *   ou une autre liste via `partners`.
 *
 * @author Mika
 * @since  2026-05-04
 * @author Mika
 * @since  2026-05-07 (refonte conforme Stitch, image composite)
 */

import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useBrandingLogos } from '@/hooks/useBrandingLogos';
import styles from './BandeauPartenaires.module.css';

/** Description d'un logo partenaire (mode legacy 6 logos individuels). */
export interface PartnerLogo {
  /** Nom officiel pour alt-text et titre de lien. */
  name: string;
  /** Chemin du logo (sous /public ou URL absolue). */
  src: string;
  /** Site partenaire, null = pas de lien (logo informatif uniquement). */
  href?: string | null;
}

export interface BandeauPartenairesProps {
  /**
   * Liste de logos individuels (mode legacy). Si fourni, on rend le
   * bandeau ancien format avec un fallback gracieux quand un PNG est
   * manquant (au lieu de l'icône brisée du navigateur).
   *
   * Si OMIS (cas par défaut depuis 2026-05-07) → on rend l'image
   * composite Stitch officielle, conforme au design.
   */
  partners?: PartnerLogo[];
  /** Mention au-dessus du bandeau (cf. §13.5.4). */
  heading?: string;
}

/**
 * Bandeau de logos du footer.
 *
 * Multi-salon (2026-07-06) : par défaut, il rend la MÊME liste de logos
 * 100 % configurable au back-office que le Header (setting
 * `branding.header_logos`, via `useBrandingLogos`). Une seule édition
 * s'applique partout, plus aucun logo (ni ruban composite) en dur.
 * Repli = liste VIDE → la section entière est masquée (jamais le logo
 * d'un autre salon).
 *
 * Le mode `partners` (liste explicite) reste disponible pour un appelant
 * qui voudrait un jeu de logos distinct.
 */
export function BandeauPartenaires({
  partners,
  heading,
}: BandeauPartenairesProps) {
  const intl = useIntl();
  const brandingLogos = useBrandingLogos();
  const resolvedHeading =
    heading ?? intl.formatMessage({ id: 'footer.partners.heading' });

  // Mode legacy : liste de logos explicitement fournie par l'appelant.
  if (partners) {
    return (
      <section className={styles.bandeau} aria-labelledby="bandeau-partenaires-titre">
        <h2 id="bandeau-partenaires-titre" className={styles.heading}>
          {resolvedHeading}
        </h2>
        <ul className={styles.list}>
          {partners.map((p) => (
            <PartnerItem key={p.name} partner={p} />
          ))}
        </ul>
      </section>
    );
  }

  // Mode par défaut : logos de marque configurables. Rien si liste vide.
  if (brandingLogos.length === 0) return null;

  return (
    <section className={styles.bandeau} aria-labelledby="bandeau-partenaires-titre">
      <h2 id="bandeau-partenaires-titre" className={styles.heading}>
        {resolvedHeading}
      </h2>
      <ul className={styles.list}>
        {brandingLogos.map((logo) => (
          <li key={logo.url} className={styles.item}>
            <img
              src={logo.url}
              alt={logo.alt}
              className={styles.logo}
              loading="lazy"
              width={120}
              height={48}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Mode legacy : 1 partenaire = 1 <img>                                   */
/* ────────────────────────────────────────────────────────────────────── */

/**
 * Affiche UN logo partenaire avec fallback gracieux : si le PNG ne charge
 * pas (404), on remplace l'image cassée par un chip texte stylé qui
 * porte le nom du partenaire. Évite le hideux « icône brisée + alt-text »
 * du navigateur.
 */
function PartnerItem({ partner }: { partner: PartnerLogo }) {
  const [imageFailed, setImageFailed] = useState(false);

  const content = imageFailed ? (
    <span className={styles.fallbackChip} title={partner.name}>
      {partner.name}
    </span>
  ) : (
    <img
      src={partner.src}
      alt={partner.name}
      className={styles.logo}
      loading="lazy"
      width={120}
      height={48}
      onError={() => setImageFailed(true)}
    />
  );

  return (
    <li className={styles.item}>
      {partner.href ? (
        <a href={partner.href} target="_blank" rel="noopener noreferrer" title={partner.name}>
          {content}
        </a>
      ) : (
        content
      )}
    </li>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Notes pour rétro-port NOLAS :
//  - NOLAS Tracking n'avait pas de bandeau permanent. Ce composant est
//    réutilisable tel quel pour ajouter une rangée de logos sur ses pages
//    institutionnelles. Pour NOLAS, fournir simplement une autre image
//    composite via `ribbonSrc`.
// Idées d'évolution :
//  - Variante mode sombre avec image composite alternative (logos
//    blancs sur fond rouge profond TNE).
//  - Animation mix-blend-multiply hover (déjà présente dans Stitch
//    `opacity-90 hover:opacity-100`).
// ──────────────────────────────────────────────────────────────────────────
